import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Review {
  _id: string;
  authorName: string;
  reviewText: string;
  rating: number;
  role?: string;
  source: 'website' | 'google_maps';
  createdAt: string;
}



interface ReviewStats {
  averageRating: number;
  totalReviews: number;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const Testimonials: React.FC = () => {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [, setStats] = useState<ReviewStats>({ averageRating: 0, totalReviews: 0 });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [formMessage, setFormMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const [formData, setFormData] = useState({
    authorName: '',
    email: '',
    rating: 5,
    reviewText: ''
  });

  // Fetch reviews on component mount
  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const response = await fetch(`${API_BASE_URL}/api/reviews?limit=50`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (process.env.NODE_ENV !== 'production') console.warn(`Failed to fetch reviews: ${response.status} ${response.statusText}`);
        setReviews([]);
        return;
      }

      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setReviews(data.data);
        setStats({
          averageRating: data.stats?.averageRating || 0,
          totalReviews: data.stats?.totalReviews || 0,
        });
      }
    } catch (error: any) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching reviews:', error);
      // Don't show error message for fetching, just continue with empty reviews
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'rating' ? parseInt(value) : value
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    if (!formData.authorName.trim()) {
      errors.authorName = 'Name is required';
    } else if (formData.authorName.length < 2) {
      errors.authorName = 'Name must be at least 2 characters';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please provide a valid email address';
    }

    if (!formData.reviewText.trim()) {
      errors.reviewText = 'Review is required';
    } else if (formData.reviewText.length < 10) {
      errors.reviewText = 'Review must be at least 10 characters';
    } else if (formData.reviewText.length > 1000) {
      errors.reviewText = 'Review cannot exceed 1000 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setFormMessage({ type: 'error', text: 'Please fix the errors below' });
      return;
    }

    setSubmitting(true);
    setFormMessage(null);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

      const response = await fetch(`${API_BASE_URL}/api/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          authorName: formData.authorName.trim(),
          email: formData.email.trim(),
          rating: formData.rating,
          reviewText: formData.reviewText.trim(),
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Try to parse response as JSON
      let data: any = {};
      try {
        data = await response.json();
      } catch (parseError) {
        if (process.env.NODE_ENV !== 'production') console.warn('Response was not valid JSON:', parseError);
        // If response is ok but not JSON, treat as success
        if (response.ok) {
          data = { success: true };
        } else {
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
      }

      if (response.ok && data.success) {
        // SUCCESS: Show success message
        setFormMessage({
          type: 'success',
          text: 'Thank you for your review! It is now displayed on the website.'
        });
        setFormData({ authorName: '', email: '', rating: 5, reviewText: '' });
        setFormErrors({});
        setShowForm(false);
        
        // Refresh reviews list immediately
        await fetchReviews();

        // Auto-dismiss success message after 5 seconds
        setTimeout(() => {
          setFormMessage(null);
        }, 5000);
        
        if (process.env.NODE_ENV !== 'production') console.log('✅ Review submitted successfully');
      } else if (!response.ok) {
        // SERVER ERROR: Show specific error from backend or generic message
        if (data.errors) {
          setFormErrors(data.errors);
        }
        const errorMessage = data.message || 'Failed to submit review. Please try again.';
        setFormMessage({
          type: 'error',
          text: errorMessage
        });
        if (process.env.NODE_ENV !== 'production') console.error('❌ Backend returned error:', data);
      } else {
        // Unexpected response
        setFormMessage({
          type: 'error',
          text: 'Unexpected response from server. Please try again.'
        });
        if (process.env.NODE_ENV !== 'production') console.warn('⚠️ Unexpected response:', data);
      }
    } catch (error: any) {
      if (process.env.NODE_ENV !== 'production') console.error('❌ Error submitting review:', error);
      
      // Differentiate between different types of errors
      let errorMessage = 'Something went wrong. Please try again.';
      
      if (error.name === 'AbortError') {
        errorMessage = 'Request timeout. Please check your internet connection and try again.';
      } else if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        errorMessage = 'Network error. Please check if you are connected to the internet.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      setFormMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setSubmitting(false);
    }
  };

  const allTestimonials: Review[] = reviews;
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || allTestimonials.length === 0) return;

    const singleWidth = track.scrollWidth / 2 || 0;
    let x = 0;
    let rafId: number | null = null;
    let lastTime = performance.now();
    const speed = 120;

    const step = (time: number) => {
      const dt = time - lastTime;
      lastTime = time;
      if (!isPaused) {
        x -= (speed * dt) / 1000;
        if (Math.abs(x) >= singleWidth) {
          x += singleWidth;
        }
        track.style.transform = `translateX(${x}px)`;
      }
      rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [allTestimonials.length, isPaused]);

  // Render rating stars
  const renderStars = (rating: number) => {
    return (
      <div style={{ display: 'flex', gap: '4px', marginBottom: '0.5rem' }}>
        {[...Array(5)].map((_, i) => (
          <span key={i} style={{ color: i < rating ? '#ffc107' : '#ddd' }}>
            ★
          </span>
        ))}
      </div>
    );
  };

  return (
    <section style={{backgroundColor: 'var(--cream)'}}>
      <div className="container">
        <div className="section-title">
          <h2>{t('testimonials_heading')}</h2>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>{t('loading_reviews') || 'Loading reviews...'}</p>
          </div>
        ) : allTestimonials.length > 0 ? (
          <div className="testimonials-marquee">
            <div
              className="testimonials-track"
              ref={trackRef}
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
              aria-hidden={false}
            >
              {allTestimonials.map((review, idx) => (
                <div key={`t1-${review._id}`} className="testimonial">
                  {review.source === 'google_maps' && (
                    <span style={{ 
                      display: 'inline-block', 
                      fontSize: '0.75rem', 
                      backgroundColor: '#4285f4', 
                      color: 'white', 
                      padding: '2px 6px',
                      borderRadius: '3px',
                      marginBottom: '0.5rem'
                    }}>
                      Google
                    </span>
                  )}
                  {renderStars(review.rating)}
                  <p className="testimonial-text">{review.reviewText}</p>
                  <div className="testimonial-author">- {review.authorName}</div>
                  {review.role && (
                    <div className="testimonial-role">{review.role}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            <p>{t('no_reviews_yet') || 'No reviews yet. Be the first to share your experience!'}</p>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button
            className="btn btn-primary"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? t('review_toggle_cancel') : t('review_toggle_show')}
          </button>
        </div>

        {formMessage && (
          <div style={{
            backgroundColor: formMessage.type === 'success' ? '#d4edda' : '#f8d7da',
            color: formMessage.type === 'success' ? '#155724' : '#721c24',
            padding: '1rem',
            borderRadius: '8px',
            marginTop: '1.5rem',
            textAlign: 'center',
            border: `1px solid ${formMessage.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
          }}>
            {formMessage.text}
          </div>
        )}

        {showForm && (
          <div className="review-form-container" style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '12px',
            marginTop: '2rem',
            boxShadow: 'var(--shadow-md)'
          }}>
            <h3 style={{ marginBottom: '1.5rem', color: 'var(--primary-dark)' }}>{t('share_experience_title')}</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="authorName" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  {t('review_name_placeholder')} *
                </label>
                <input
                  type="text"
                  id="authorName"
                  name="authorName"
                  placeholder={t('review_name_placeholder')}
                  value={formData.authorName}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: formErrors.authorName ? '2px solid #dc3545' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
                {formErrors.authorName && <span style={{ color: '#dc3545', fontSize: '0.875rem' }}>{formErrors.authorName}</span>}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="email" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  {t('email_address_label')} (Optional)
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder={t('email_address_label')}
                  value={formData.email}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: formErrors.email ? '2px solid #dc3545' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
                {formErrors.email && <span style={{ color: '#dc3545', fontSize: '0.875rem' }}>{formErrors.email}</span>}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="rating" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  {t('rating_label') || 'Rating'} *
                </label>
                <select
                  id="rating"
                  name="rating"
                  value={formData.rating}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value={5}>★★★★★ - Excellent</option>
                  <option value={4}>★★★★ - Very Good</option>
                  <option value={3}>★★★ - Good</option>
                  <option value={2}>★★ - Fair</option>
                  <option value={1}>★ - Poor</option>
                </select>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="reviewText" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  {t('review_text_placeholder')} * ({formData.reviewText.length}/1000)
                </label>
                <textarea
                  id="reviewText"
                  name="reviewText"
                  placeholder={t('review_text_placeholder')}
                  value={formData.reviewText}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: formErrors.reviewText ? '2px solid #dc3545' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
                {formErrors.reviewText && <span style={{ color: '#dc3545', fontSize: '0.875rem' }}>{formErrors.reviewText}</span>}
              </div>


              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
                style={{ width: '100%' }}
              >
                {submitting ? (t('submitting') || 'Submitting...') : t('submit_review')}
              </button>
            </form>
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;
